import getSignaturePrintStatus from 'utils/shared/trades/getSignaturePrintStatus';
import { getIsGoldenDarkPool } from 'utils/shared/trades/getIsGoldenDarkPool';
import { SignaturePrintStatus } from 'utils/shared/trades/types';

export interface DarkPoolTrade {
  ticker?: string;
  amount?: number;
  size?: number;
  avgVolume?: number;
  open?: number;
  date?: string;
  [key: string]: any;
}

export interface DarkPoolSummary {
  ticker: string;
  premium: number;
  volume: number;
  lateBuyCount: number;
  lateSellCount: number;
  goldenTradeCount: number;
  totalTradeCount: number;
}

export interface DarkPoolSummaryWithTrades extends DarkPoolSummary {
  trades: DarkPoolTrade[];
}

export enum TimeFilters {
  Today,
  Week,
  TwoWeeks,
  Month,
}

const WEEK = 7 * 24 * 60 * 60 * 1000;

export function filterTradesByTime(
  trades: DarkPoolTrade[],
  timeFilter: TimeFilters,
): DarkPoolTrade[] {
  if (trades.length === 0) {
    return trades;
  }

  const maxDateString = trades[0]?.date;
  if (!maxDateString) {
    return trades;
  }

  const maxDateTimestamp = new Date(maxDateString).getTime();

  switch (timeFilter) {
    case TimeFilters.Today:
      return trades.filter((t) => t.date === maxDateString);
    case TimeFilters.Week:
      return trades.filter((t) => {
        if (!t.date) return false;
        return maxDateTimestamp - new Date(t.date).getTime() <= WEEK;
      });
    case TimeFilters.TwoWeeks:
      return trades.filter((t) => {
        if (!t.date) return false;
        return maxDateTimestamp - new Date(t.date).getTime() <= 2 * WEEK;
      });
    case TimeFilters.Month:
      return trades;
    default:
      return trades;
  }
}

export function calculateDarkPoolSummary(
  trades: DarkPoolTrade[],
  timeFilter: TimeFilters = TimeFilters.Today,
  includeTrades: boolean = false,
): DarkPoolSummary | DarkPoolSummaryWithTrades {
  const filteredTrades = filterTradesByTime(trades, timeFilter);

  const summary: DarkPoolSummary = {
    ticker: trades[0]?.ticker || '',
    premium: 0,
    volume: 0,
    lateBuyCount: 0,
    lateSellCount: 0,
    goldenTradeCount: 0,
    totalTradeCount: filteredTrades.length,
  };

  const open = filteredTrades[0]?.open || 0;

  filteredTrades.forEach((trade) => {
    const signaturePrintStatus = getSignaturePrintStatus(trade, { open });
    summary.premium += trade.amount || 0;
    summary.volume += trade.size || 0;

    if (signaturePrintStatus === SignaturePrintStatus.Buy) {
      summary.lateBuyCount += 1;
    } else if (signaturePrintStatus === SignaturePrintStatus.Sell) {
      summary.lateSellCount += 1;
    }

    if (getIsGoldenDarkPool(trade.size || 0, trade.avgVolume || 0)) {
      summary.goldenTradeCount += 1;
    }
  });

  if (includeTrades) {
    return {
      ...summary,
      trades: filteredTrades,
    };
  }

  return summary;
}
